import React, { PureComponent } from 'react';
import * as dates from '@simplybenefits/sb-dates';
import { connect } from 'react-redux';
import PropTypes from 'prop-types';
import Grid from '@mui/material/Grid';
import {
    Button,
    TextInput,
    Services,
    Title,
    Modal,
    Text,
    ContentBlock,
    BubbleSelectBlock, DateInput
} from '@simplybenefits/sb-ui-library';
import { editAdvisor, resetPassword, deleteMarketplaceAffiliateLink, createMarketplaceAffiliateLink } from '../../actions/advisors.js';
import PermissionsComponent from '../../components/PermissionsComponent.js';
import FormControl from "@mui/material/FormControl";
import IconButton from "@mui/material/IconButton";
import { DeleteOutline as DeleteIcon } from '@mui/icons-material';

const statusMapString = {
    1: 'Active',
    2: 'Disabled',
    9: 'Cancelled'
};

const statusMapInt = {
    Active: 1,
    Disabled: 2,
    Cancelled: 9
};
class Settings extends PureComponent {
    state = {
        firstname: '',
        lastname: '',
        email: '',
        company: '',
        language: '',
        status: '',
        resetPasswordModal: false,
        useMFA: false,
        marketplaceAffiliateLink: this.props.meta.marketplaceAffiliateLink || {},
        marketplaceAffiliateGroupNumber: null,
        marketplaceAffiliateExpiry: null
    };

    componentDidMount() {
        const { meta } = this.props;

        this.setState({
            firstname: meta.firstname,
            lastname: meta.lastname,
            email: meta.email,
            company: meta.company,
            language: meta.language === 'fr' ? 'French' : 'English',
            status: statusMapString[meta.status],
            useMFA: meta.useMFA || false,
            marketplaceAffiliateLink: meta.marketplaceAffiliateLink
        });
    }

    componentDidUpdate(prevProps) {
        // Re-hydrate when Redux meta changes (e.g., after create/delete affiliate link)
        const prev = prevProps.meta;
        const next = this.props.meta;

        // Use a cheap guard: update if the ref changed OR key fields changed
        if (prev !== next || (prev?.marketplaceAffiliateLink?.affiliateLinkID !== next?.marketplaceAffiliateLink?.affiliateLinkID)) {
            this.setState({
                marketplaceAffiliateLink: next.marketplaceAffiliateLink || {}
            });
        }
    }

    _goBack = () => {
        this.props.goBack(null);
    };

    _onBlur = async (type) => {
        this.setState({
            [`${ type }Error`]: await Services.validateInput(type, this.state[type])
        });
    };

    _handleInputChange = (type, event, isValue = false) => {
        this.setState({
            [type]: isValue ? event : event.target.value
        });
    };

    _toggle = (type) => {
        if (type === 'affiliateLinkModal') {
            this.setState({
                marketplaceAffiliateGroupNumber: null,
                marketplaceAffiliateExpiry: null,
                [type]: !this.state[type]
            });
        } else {
            this.setState({
                [type]: !this.state[type]
            });
        }
    };

    _validateForm = async () => {
        const firstnameError = await Services.validateInput('firstname', this.state.firstname);
        const lastnameError = await Services.validateInput('lastname', this.state.lastname);
        const emailError = await Services.validateInput('email', this.state.email);
        const companyError = await Services.validateInput('company', this.state.company);
        const statusError = await Services.validateInput('status', this.state.status);
        const languageError = await Services.validateInput('language', this.state.language);

        this.setState({
            firstnameError,
            lastnameError,
            emailError,
            companyError,
            languageError,
            statusError
        });

        if (
            firstnameError ||
            lastnameError ||
            emailError ||
            companyError ||
            languageError ||
            statusError
        ) {
            return false;
        }

        return true;
    };

    _saveUser = async () => {
        const isFormValid = await this._validateForm();
        const {
            firstname,
            lastname,
            email,
            company,
            language,
            status,
            useMFA
        } = this.state;

        if (!isFormValid) {
            return false;
        }

        const body = {
            firstname,
            lastname,
            email,
            company,
            language: language === "French" ? 'fr' : 'en',
            status: statusMapInt[status],
            useMFA: useMFA || false
        };

        this.props.dispatch(editAdvisor(this.props.meta.advisorID, body));

        return true;
    };

    _resetPassword = async () => {
        const success = await this.props.dispatch(resetPassword({ email: this.state.email }));

        if (success) {
            this._toggle('resetPasswordModal');
        }
    };

    _createAffiliateLink = async () => {
        const marketplaceAffiliateGroupNumberError = await Services.validateInput('checkEmpty', this.state.marketplaceAffiliateGroupNumber);

        let marketplaceAffiliateExpiryError = null;

        if (this.state.marketplaceAffiliateExpiry) {
            marketplaceAffiliateExpiryError = await Services.validateInput('effectivedate', this.state.marketplaceAffiliateExpiry);
        }

        this.setState({
            marketplaceAffiliateGroupNumberError,
            marketplaceAffiliateExpiryError
        });

        if (marketplaceAffiliateGroupNumberError || marketplaceAffiliateExpiryError) {
            return false;
        }

        const success = await this.props.dispatch(createMarketplaceAffiliateLink(
            this.props.meta.advisorID,
            this.state.marketplaceAffiliateGroupNumber,
            this.state.marketplaceAffiliateExpiry ? Services.dates.formatOutgoingDate(this.state.marketplaceAffiliateExpiry) : null
        ));

        if (success) {
            this._toggle('affiliateLinkModal');
        }

        return true;
    };

    render() {
        const environment = process.env.REACT_APP_ENVIRONMENT;
        let portalUrl = null;

        switch (environment) {
            case 'local':
                portalUrl = `http://localhost:3000`;
                break;
            case 'staging':
                portalUrl = 'https://member-staging.simplybenefits.ca';
                break;
            case 'production':
                portalUrl = 'https://member.simplybenefits.ca';
                break;
            default:
                break;
        }

        return (
            <React.Fragment>
                <Title title="Advisor Settings" />

                <ContentBlock>
                    <Grid
                        container
                        direction="row"
                        alignItems="center"
                        spacing={2}
                    >
                        <Grid item xs={12} sm={6} md={3}>
                            <TextInput
                                label="First Name"
                                value={this.state.firstname}
                                onBlur={() => this._onBlur('firstname')}
                                onChange={(value) => this._handleInputChange('firstname', value)}
                                error={Boolean(this.state.firstnameError)}
                                helperText={this.state.firstnameError}
                                fullWidth
                                required
                                autoFocus
                            />
                        </Grid>

                        <Grid item xs={12} sm={6} md={3}>
                            <TextInput
                                label="Last Name"
                                value={this.state.lastname}
                                onBlur={() => this._onBlur('lastname')}
                                onChange={(value) => this._handleInputChange('lastname', value)}
                                error={Boolean(this.state.lastnameError)}
                                helperText={this.state.lastnameError}
                                fullWidth
                                required
                            />
                        </Grid>

                        <Grid item xs={12} sm={6} md={3}>
                            <TextInput
                                label="Email"
                                value={this.state.email}
                                onBlur={() => this._onBlur('email')}
                                onChange={(value) => this._handleInputChange('email', value)}
                                error={Boolean(this.state.emailError)}
                                helperText={this.state.emailError}
                                fullWidth
                                required
                            />
                        </Grid>

                        <Grid item xs={12} sm={6} md={3}>
                            <TextInput
                                label="Company"
                                value={this.state.company}
                                onBlur={() => this._onBlur('company')}
                                onChange={(value) => this._handleInputChange('company', value)}
                                error={Boolean(this.state.companyError)}
                                helperText={this.state.companyError}
                                fullWidth
                                required
                            />
                        </Grid>

                        <Grid item xs={12} sm={6} md={3}>
                            <TextInput
                                label="Language"
                                value={ this.state.language }
                                onBlur={() => this._onBlur('language')}
                                onChange={(value) => this._handleInputChange('language', value)}
                                error={Boolean(this.state.languageError)}
                                helperText={this.state.languageError}
                                select
                                required
                                selectOptions={[ 'English', 'French' ]}
                                fullWidth
                            />
                        </Grid>

                        <Grid item xs={12} sm={6} md={3}>
                            <TextInput
                                label="Status"
                                // variant="outlined"
                                value={this.state.status}
                                onBlur={() => this._onBlur('status')}
                                onChange={(value) => this._handleInputChange('status', value)}
                                error={Boolean(this.state.statusError)}
                                helperText={this.state.statusError}
                                required
                                select
                                fullWidth
                                selectOptions={[
                                    {
                                        key: '1',
                                        value: 'Active'
                                    },
                                    {
                                        key: '2',
                                        value: 'Disabled'
                                    },
                                    {
                                        key: '9',
                                        value: 'Cancelled'
                                    }
                                ]}
                            />
                        </Grid>
                    </Grid>

                    <Grid
                        container
                        direction="row"
                        alignItems="baseline"
                        spacing={2}
                        style={{ marginTop: 10, marginLeft: 5 }}
                    >
                        <FormControl component="fieldset">
                            <Text
                                medium
                                bold
                                margin="20px 0 0px 0"
                            >
                                Enforce Multi-factor Authentication (MFA)
                            </Text>

                            <Text
                                tiny
                                light
                                margin="5px 0 5px 0"
                            >
                                Enforce multi-factor authentication when attempting login.
                            </Text>

                            <BubbleSelectBlock
                                first={{
                                    title: 'Yes',
                                    selected: Boolean(this.state.useMFA) === true,
                                    field: 'enforceMFAYes'
                                }}
                                second={{
                                    title: 'No',
                                    selected: Boolean(this.state.useMFA) === false,
                                    field: 'enforceMFANo'
                                }}
                                onBlur={(e) => this._onBlur('useMFA', e)}
                                handleChange={ async (field) => {
                                    if (field === 'enforceMFAYes') {
                                        await this._handleInputChange('useMFA', true, true);
                                    } else {
                                        await this._handleInputChange('useMFA', false, true);
                                    }
                                }}
                            />
                        </FormControl>
                    </Grid>

                    <Grid
                        container
                        direction="row"
                        alignItems="baseline"
                        spacing={2}
                        style={{ marginTop: 10, marginLeft: 5 }}
                    >
                        <FormControl component="fieldset">
                            <Text
                                medium
                                bold
                                margin="20px 0 0px 0"
                            >
                                Marketplace Affiliate Link
                            </Text>

                            {
                                this.state.marketplaceAffiliateLink.affiliateLinkID ?
                                    <div>
                                        <Text
                                            small
                                            margin="5px 0 5px 0"
                                        >
                                            Use the link below to enrol members into the specified marketplace only group.
                                        </Text>

                                        <div style={{
                                            display: 'block',
                                            position: 'relative',
                                            background: '#fff',
                                            padding: "10px 20px",
                                            borderRadius: '8px',
                                            boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
                                            border: '1px solid #eee',
                                            marginTop: "20px",
                                            width: "750px"
                                        }}>
                                            <IconButton
                                                aria-label="Delete"
                                                onClick={ () => this._toggle('affiliateLinkDeleteConfirmModal') }
                                                style={{
                                                    position: 'absolute',
                                                    top: '10px',
                                                    right: '10px',
                                                    background: 'transparent',
                                                    border: 'none',
                                                    cursor: 'pointer',
                                                    color: '#888'
                                                }}
                                            >
                                                <DeleteIcon />
                                            </IconButton>

                                            <Text small bold margin="5px 0 5px 0">
                                                URL
                                            </Text>

                                            <Text small light margin="5px 0 5px 0">
                                                { `${ portalUrl }/signup/affiliate/marketplace/${ this.state.marketplaceAffiliateLink.affiliateLinkID }` }
                                            </Text>

                                            <Text small bold margin="5px 0 5px 0">
                                                Group Number
                                            </Text>

                                            <Text small light margin="5px 0 5px 0">
                                                { this.state.marketplaceAffiliateLink.groupNum }
                                            </Text>

                                            <Text small bold margin="5px 0 5px 0">
                                                Expiry
                                            </Text>

                                            <Text small light margin="5px 0 5px 0">
                                                { this.state.marketplaceAffiliateLink.expiry ? dates.getUTC(this.state.marketplaceAffiliateLink.expiry, 'MMM DD, YYYY') : null }
                                            </Text>
                                        </div>
                                    </div>
                                    :
                                    <div>
                                        <Text
                                            small
                                            light
                                            margin="5px 0 5px 0"
                                        >
                                            Use the button below to create a Marketplace only affiliate link.
                                        </Text>

                                        <Button
                                            onClick={() => this._toggle('affiliateLinkModal')}
                                            transparent
                                            customStyle={{ marginLeft: "-10px" }}
                                        >
                                            Create Affiliate Link
                                        </Button>
                                    </div>
                            }

                        </FormControl>
                    </Grid>

                    <Grid
                        container
                        direction="row"
                        alignItems="baseline"
                        justifyContent="flex-end"
                        spacing={2}
                        style={{ marginTop: 10 }}
                    >
                        <PermissionsComponent
                            permission="Advisor Settings"
                            accessType={this.props.accessType}
                            level="write"
                        >
                            <div>
                                <Button
                                    onClick={() => this._toggle('resetPasswordModal')}
                                    transparent
                                    customStyle={{ marginRight: 1.25 }}
                                >
                                    Send Reset Password Email
                                </Button>

                                <Button
                                    onClick={this._saveUser}
                                    color="primary"
                                >
                                    Save
                                </Button>
                            </div>
                        </PermissionsComponent>
                    </Grid>

                    <Modal
                        open={this.state.resetPasswordModal}
                        onClose={() => this._toggle('resetPasswordModal')}
                        title="Reset Password"
                        primaryButton={{
                            label: "Continue",
                            onClick: this._resetPassword
                        }}
                        loader={this.props.loader}
                    >
                        <Text medium light>
                            You are about to send an email with a forgotten password link, are you sure you want to continue? This cannot be undone.
                        </Text>
                    </Modal>

                    <Modal
                        open={ this.state.affiliateLinkDeleteConfirmModal }
                        onClose={() => this._toggle('affiliateLinkDeleteConfirmModal')}
                        title="Remove Affiliate Link"
                        primaryButton={{
                            label: 'Remove Affiliate Link',
                            onClick: async () => {
                                const success = await this.props.dispatch(deleteMarketplaceAffiliateLink(this.props.meta.advisorID));

                                if (success) {
                                    this._toggle('affiliateLinkDeleteConfirmModal');
                                }
                            }
                        }}
                        loader={this.props.loader}
                    >
                        <Text medium>
                            Are you sure you want to remove this affiliate link? This action cannot be undone.
                        </Text>
                    </Modal>

                    <Modal
                        open={this.state.affiliateLinkModal}
                        onClose={() => this._toggle('affiliateLinkModal')}
                        title="Create Affiliate Link"
                        primaryButton={{
                            label: "Save",
                            onClick: this._createAffiliateLink
                        }}
                        loader={ this.props.loader }
                    >
                        <TextInput
                            label="Group Number"
                            value={ this.state.marketplaceAffiliateGroupNumber }
                            onChange={(value) => this._handleInputChange('marketplaceAffiliateGroupNumber', value)}
                            error={Boolean(this.state.marketplaceAffiliateGroupNumberError)}
                            helperText={this.state.marketplaceAffiliateGroupNumberError}
                            fullWidth
                            required
                        />

                        <DateInput
                            label="Expiry Date"
                            value={ this.state.marketplaceAffiliateExpiry }
                            onBlur={(e) => this._handleInputChange('marketplaceAffiliateExpiry', e)}
                            fullWidth
                            required
                        />
                    </Modal>

                    <span id="advisor-settings" />
                </ContentBlock>
            </React.Fragment>
        );
    }
}

Settings.propTypes = {
    currentAdvisor: PropTypes.object,
    accessType: PropTypes.string
};

/**
 *
 * @param {*} state
 */
const mapStateToProps = (state) => ({
    loader: state.loader,
    meta: state.advisors.currentAdvisor,
    accessType: state.master.access
});

export default connect(mapStateToProps)(Settings);